import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Plus,
  Sun,
  Moon,
  Palette,
  Check,
  X
} from 'lucide-react';
import { ColorPicker } from '@/components/ui/color-picker';
import { ApiWidget, ReportTheme } from '@/pages/Reports/Generate/types';

interface SubSidebarProps {
  availableWidgets: ApiWidget[];
  selectedWidgetIds: Set<string>;
  isLoadingWidgets: boolean;
  reportTheme: ReportTheme;
  isOpen: boolean;
  onWidgetSelection: (widget: ApiWidget, checked: boolean) => void;
  onThemeChange: (theme: ReportTheme) => void;
  onClose: () => void;
}

const SubSidebar: React.FC<SubSidebarProps> = ({
  availableWidgets,
  selectedWidgetIds,
  isLoadingWidgets,
  reportTheme,
  isOpen,
  onWidgetSelection,
  onThemeChange,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<'widgets' | 'theme'>('widgets');

  const handleThemeToggle = (theme: 'light' | 'dark') => {
    onThemeChange({ ...reportTheme, theme });
  };

  const handleColorChange = (color: string) => {
    onThemeChange({ ...reportTheme, primaryColor: color });
  };

  return (
    <>
      {/* Panel */}
      <div className={`fixed right-0 top-0 h-full w-80 bg-background border-l shadow-xl transform transition-transform duration-300 z-40 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="h-full flex flex-col p-8 pt-0">
          {/* Header */}
          <div className="flex-shrink-0 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div className="space-y-1">
                <h2 className="text-2xl font-bold text-muted-foreground">Settings</h2>
                <p className="text-sm text-secondary-foreground">
                  Customize your report widgets and theme
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0 hover:bg-muted/50 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Tab Navigation */}
            <div className="flex border-b border-border">
              <button
                onClick={() => setActiveTab('widgets')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'widgets'
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                }`}
              >
                <Plus className="h-4 w-4" />
                Widgets
              </button>
              <button
                onClick={() => setActiveTab('theme')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'theme'
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                }`}
              >
                <Palette className="h-4 w-4" />
                Theme
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'widgets' && (
              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-muted-foreground">Available Widgets</h3>
                    <div className="text-xs text-secondary-foreground">
                      {selectedWidgetIds.size} of {availableWidgets.length} selected
                    </div>
                  </div>
                  {isLoadingWidgets ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-secondary-foreground">Loading widgets...</div>
                    </div>
                  ) : availableWidgets.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-center">
                        <div className="text-muted-foreground mb-2">No widgets available</div>
                        <div className="text-xs text-secondary-foreground">
                          Check your backend configuration
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {availableWidgets.map((widget) => {
                        const isSelected = selectedWidgetIds.has(widget.id);
                        return (
                          <Card
                            key={widget.id}
                            className={`cursor-pointer transition-all duration-200 hover:shadow-md relative bg-card border-border ${
                              isSelected
                                ? 'ring-1 ring-primary/30 bg-primary/5 border-primary/10'
                                : 'hover:bg-muted/50'
                            }`}
                            onClick={() => onWidgetSelection(widget, !isSelected)}
                          >
                            {/* Selection indicator */}
                            {isSelected && (
                              <div className="absolute top-2 right-2 w-4 h-4 bg-primary/20 rounded-full flex items-center justify-center">
                                <Check className="h-2.5 w-2.5 text-primary" />
                              </div>
                            )}
                            
                            <CardContent className="p-3">
                              <div className="text-center">
                                <div className={`w-8 h-8 mx-auto mb-2 rounded-lg flex items-center justify-center ${
                                  isSelected 
                                    ? 'bg-primary/15 text-primary' 
                                    : 'bg-primary/10 text-primary'
                                }`}>
                                  {isSelected ? (
                                    <Check className="h-4 w-4" />
                                  ) : (
                                    <Plus className="h-4 w-4" />
                                  )}
                                </div>
                                <div className="text-xs font-medium  mb-1 text-foreground">
                                  {widget.metric}
                                </div>
                                {/* <div className="text-xs text-muted-foreground line-clamp-2">
                                  {widget.business_value}
                                </div> */}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'theme' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-4">Theme Settings</h3>
                  
                  {/* Theme Toggle */}
                  <div className="space-y-3">
                    <label className="text-xs font-medium text-muted-foreground">Theme Mode</label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={reportTheme.theme === 'light' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleThemeToggle('light')}
                        className="flex items-center gap-2 h-10 text-sm"
                      >
                        <Sun className="h-4 w-4" />
                        Light
                      </Button>
                      <Button
                        variant={reportTheme.theme === 'dark' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleThemeToggle('dark')}
                        className="flex items-center gap-2 h-10 text-sm"
                      >
                        <Moon className="h-4 w-4" />
                        Dark
                      </Button>
                    </div>
                  </div>

                  {/* Primary Color Picker */}
                  <div className="space-y-3">
                    <label className="text-xs font-medium text-muted-foreground">Primary Color</label>
                    <ColorPicker
                      value={reportTheme.primaryColor}
                      onChange={handleColorChange}
                      label=""
                      showGradient={true}
                    />
                  </div>

                  {/* Color Preview */}
                  <div className="space-y-3">
                    <label className="text-xs font-medium text-muted-foreground">Preview</label>
                    <div className="p-3 rounded-lg border border-border" style={{ backgroundColor: reportTheme.primaryColor }}>
                      <div className="text-white text-xs font-medium">
                        Sample text with primary color
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SubSidebar;

